import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Alert,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LockIcon from '@mui/icons-material/Lock';
import useSessionDetails from '../hooks/useSessionDetails';

const SessionDetailsDialog = ({
  open,
  onClose,
  sessionId,
  formatTime,
  token,
}) => {
  // Fetch session details using the sessionId
  const {
    surveySession: sessionData,
    isLoading,
    error,
  } = useSessionDetails(sessionId, token);

  if (!sessionId) return null;

  const getSurveyStatusText = (status) => {
    switch (status) {
      case 0:
        return 'Not Started';
      case 1:
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  const getSurveyStatusColor = (status) => {
    switch (status) {
      case 0:
        return 'default';
      case 1:
        return 'success';
      default:
        return 'warning';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #C38A2D 0%, #A0721F 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{ flexGrow: 1, fontWeight: 600, color: '#FFF' }}
        >
          Session Details
          {sessionData?.customer && (
            <Typography
              variant="subtitle1"
              sx={{ opacity: 0.9, fontWeight: 400 }}
            >
              {sessionData.customer}
            </Typography>
          )}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{
          p: 3,
          backgroundColor: '#f8fafc',
        }}
      >
        {isLoading && (
          <Grid container spacing={3} mt={1}>
            {/* Customer Information Skeleton */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{ mr: 2 }}
                    />
                    <Skeleton variant="text" width={180} height={32} />
                  </Box>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Box>
                      <Skeleton
                        variant="text"
                        width={60}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={140} height={24} />
                    </Box>
                    <Box>
                      <Skeleton
                        variant="text"
                        width={80}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={100} height={24} />
                    </Box>
                    <Box>
                      <Skeleton
                        variant="text"
                        width={80}
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="rounded" width={60} height={24} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Session Information Skeleton */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{ mr: 2 }}
                    />
                    <Skeleton variant="text" width={160} height={32} />
                  </Box>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Box>
                      <Skeleton
                        variant="text"
                        width={80}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={80} height={24} />
                    </Box>
                    <Box>
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={120} height={24} />
                    </Box>
                    <Box>
                      <Skeleton
                        variant="text"
                        width={60}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={90} height={24} />
                    </Box>
                    <Box>
                      <Skeleton
                        variant="text"
                        width={60}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={40} height={24} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Date & Time Skeleton */}
            <Grid size={12}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{ mr: 2 }}
                    />
                    <Skeleton variant="text" width={120} height={32} />
                  </Box>
                  <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={160} height={24} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Skeleton
                        variant="text"
                        width={80}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={140} height={24} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Skeleton
                        variant="text"
                        width={70}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={140} height={24} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Masseuses & Survey Status Skeleton */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Skeleton
                    variant="text"
                    width={100}
                    height={32}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Skeleton variant="rounded" width={80} height={32} />
                    <Skeleton variant="rounded" width={70} height={32} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Skeleton
                    variant="text"
                    width={120}
                    height={32}
                    sx={{ mb: 2 }}
                  />
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Skeleton variant="rounded" width={100} height={32} />
                    <Box>
                      <Skeleton
                        variant="text"
                        width={90}
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={160} height={24} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '0.95rem',
              },
            }}
          >
            Failed to load session details. Please try again.
          </Alert>
        )}

        {!isLoading && !error && sessionData && (
          <Grid container spacing={3} mt={1}>
            {/* Customer Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: '#FDF4E3',
                        mr: 2,
                      }}
                    >
                      <PersonIcon sx={{ color: '#C38A2D', fontSize: 20 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                      Customer Information
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                      >
                        Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                      >
                        {sessionData.member?.name || sessionData.customer}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                      >
                        Member ID
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                      >
                        {sessionData.member?.id
                          ? sessionData.member?.id
                          : 'N/A'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}
                      >
                        Surveyable
                      </Typography>
                      <Chip
                        label={sessionData.member?.surveyable ? 'Yes' : 'No'}
                        color={
                          sessionData.member?.surveyable ? 'success' : 'default'
                        }
                        size="small"
                        sx={{
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 2,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Session Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: '#F5EFE7',
                        mr: 2,
                      }}
                    >
                      <AccessTimeIcon sx={{ color: '#A0721F', fontSize: 20 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                      Session Information
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                      >
                        Session ID
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                      >
                        {sessionData.id}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                      >
                        Session Type
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                      >
                        {sessionData.session_type?.name} minutes
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                      >
                        Score
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                      >
                        {sessionData.score || 'Not rated'}
                      </Typography>
                    </Box>

                    {sessionData.locker && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                        >
                          Locker
                        </Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <LockIcon sx={{ fontSize: 16, color: '#C38A2D' }} />
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: '#1e293b' }}
                          >
                            {sessionData.locker}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Date & Time Information */}
            <Grid size={12}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: '#F9F6F0',
                        mr: 2,
                      }}
                    >
                      <DateRangeIcon sx={{ color: '#8B5A1A', fontSize: 20 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                      Date & Time
                    </Typography>
                  </Box>

                  <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                        >
                          Working Date
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#1e293b' }}
                        >
                          {formatTime(sessionData.working_date)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                        >
                          Start Time
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#1e293b' }}
                        >
                          {formatTime(sessionData.start_time)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                        >
                          End Time
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#1e293b' }}
                        >
                          {formatTime(sessionData.end_time)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Masseuses */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}
                  >
                    Masseuses
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {sessionData.masseuses?.map((masseuse, index) => (
                      <Chip
                        key={index}
                        label={masseuse.name}
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          borderColor: '#E6D4B7',
                          color: '#8B5A1A',
                          '&:hover': {
                            backgroundColor: '#FDF4E3',
                            borderColor: '#C38A2D',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Survey Status */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}
                  >
                    Survey Status
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Chip
                      label={getSurveyStatusText(sessionData.survey_status)}
                      color={getSurveyStatusColor(sessionData.survey_status)}
                      sx={{
                        alignSelf: 'flex-start',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                    {sessionData.survey_time && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', mb: 0.5, fontWeight: 500 }}
                        >
                          Survey Time
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: '#1e293b' }}
                        >
                          {formatTime(sessionData.survey_time)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #C38A2D 0%, #A0721F 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #A0721F 0%, #8B5A1A 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(195, 138, 45, 0.3)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionDetailsDialog;
